import { Cartesian3, Cartesian4, Color, Matrix4, Transforms } from 'cesium';

// ENU column index per axis: 0 = East (X), 1 = North (Y), 2 = Up (Z)
export const GIZMO_AXES = [
	{ key: 'x', column: 0, color: Color.RED },
	{ key: 'y', column: 1, color: Color.LIME },
	{ key: 'z', column: 2, color: Color.DODGERBLUE },
] as const;

export type GizmoAxisKey = (typeof GIZMO_AXES)[number]['key'];

/** Unit world-space direction of an ENU axis at the given center. */
export function getAxisDir(center: Cartesian3, column: number): Cartesian3 {
	const enu = Transforms.eastNorthUpToFixedFrame(center);
	const col = Matrix4.getColumn(enu, column, new Cartesian4());
	const dir = new Cartesian3(col.x, col.y, col.z);
	return Cartesian3.normalize(dir, dir);
}

/**
 * Parameter t along an axis line (point + t*dir) closest to a ray (origin + s*rayDir).
 * Both dir and rayDir are assumed unit length.
 */
export function closestAxisParam(
	axisPoint: Cartesian3,
	axisDir: Cartesian3,
	rayOrigin: Cartesian3,
	rayDir: Cartesian3
): number {
	const w0 = Cartesian3.subtract(axisPoint, rayOrigin, new Cartesian3());
	const b = Cartesian3.dot(axisDir, rayDir);
	const d = Cartesian3.dot(axisDir, w0);
	const e = Cartesian3.dot(rayDir, w0);
	const denom = 1 - b * b; // a*c - b*b with a=c=1
	if (Math.abs(denom) < 1e-9) return 0;
	return (b * e - d) / denom;
}
